import { forwardRef, type Ref, useImperativeHandle, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons/faExclamationTriangle';
import { ErrorMessage } from '@hookform/error-message';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Col,
  Flex,
  Form,
  Row,
  Space,
  theme,
  Tooltip,
  Typography,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { SchedulingTimePicker } from 'pos-client/components/scheduleBoard/components/SchedulingTimePicker.tsx';
import { useScheduleBoardContext } from 'pos-client/components/scheduleBoard/context/ScheduleBoardContext.tsx';
import { useAuthorizeBlockedSlot } from 'pos-client/components/scheduleBoard/hooks/useAuthorizeBlockedSlot.ts';
import { useCreateAndAuthorizeBlockedSlot } from 'pos-client/components/scheduleBoard/hooks/useCreateAndAuthorizeBlockedSlot.ts';
import { useCreateBlockedSlot } from 'pos-client/components/scheduleBoard/hooks/useCreateBlockedSlot.ts';
import { useDeleteBlockedSlot } from 'pos-client/components/scheduleBoard/hooks/useDeleteBlockedSlot.ts';
import { useEditBlockedSlot } from 'pos-client/components/scheduleBoard/hooks/useEditBlockedSlot.ts';
import { useSelectedLocationCurrentDate } from 'pos-client/components/scheduleBoard/hooks/useSelectedLocationCurrentDate.ts';
import { useUnauthorizeBlockedSlot } from 'pos-client/components/scheduleBoard/hooks/useUnauthorizeBlockSlot.ts';
import {
  confirmBlockedSlotDelete,
  unsavedChangesModal,
} from 'pos-client/components/scheduleBoard/utils/BlockedSlotUtils.tsx';
import { PosModal } from 'pos-client/components/shared/Display/Modal/PosModal.tsx';
import { PosInput } from 'pos-client/components/shared/Form/PosInput.tsx';
import PosText from 'pos-client/components/shared/General/PosText.tsx';
import { usePermissions } from 'pos-client/hooks/usePermissions.tsx';
import { fullWidth } from 'pos-client/utils/cssHelpers.ts';
import {
  CreateBlockedSlotSchema,
  type CreateEditBlockedSlotModel,
  type EditBlockedSlotModel,
  type ScheduleBoardJobsAndBlockedSlotsModel,
} from 'pos-shared/models/schedulingSchema.ts';

dayjs.extend(customParseFormat);
dayjs.extend(isBetween);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

export type BlockedSlotModalRef = {
  openModal: (laneId: string, date: Dayjs) => void;
  openEditModal: (blockedSlotData: EditBlockedSlotModel) => void;
};

type BlockedSlotModalProps = object;

const DATE_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

const BlockedSlotModal = forwardRef(
  (_: BlockedSlotModalProps, ref: Ref<BlockedSlotModalRef>) => {
    const { token } = theme.useToken();

    const { t } = useTranslation();

    const {
      reset,
      control,
      trigger,
      formState: { isDirty, isValid, errors },
      handleSubmit,
    } = useForm<CreateEditBlockedSlotModel>({
      resolver: zodResolver(CreateBlockedSlotSchema),
      mode: 'all',
      defaultValues: {
        description: '',
      },
    });
    const formValues = useWatch({ control });
    const [open, setModalOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState<boolean>(false);

    const { selectedLocation, selectedDate, tradingTimes, boardData } =
      useScheduleBoardContext();

    const { hasPermissions } = usePermissions();
    const hasAdminPermissions = hasPermissions('authorize.blocked.slot');
    const isStoreManager = hasPermissions('is.store.manager');
    const canAddBlockedSlot = hasPermissions('add.blocked.slot');

    const blockedSlotStart = dayjs(
      formValues.startDateTime,
      'YYYY-MM-DDTHH:mm:ss'
    );
    const blockedSlotEnd = dayjs(
      formValues.finishDateTime,
      'YYYY-MM-DDTHH:mm:ss'
    );

    const areStartEndTimesSame = blockedSlotStart.isSame(blockedSlotEnd);
    const isEndTimeBeforeStart = blockedSlotEnd.isBefore(blockedSlotStart);
    const isBlockedSlotValid =
      blockedSlotStart.isValid() &&
      blockedSlotEnd.isValid() &&
      blockedSlotStart.isBefore(blockedSlotEnd);

    const currentTime = useSelectedLocationCurrentDate();
    const isStartTimeBeforeCurrentTime = dayjs(blockedSlotStart)
      .utc(true)
      .isBefore(dayjs(currentTime).utc(true));

    const isBlockedSlotWithinOrder = (
      order: ScheduleBoardJobsAndBlockedSlotsModel
    ) => {
      const orderSlotStart = dayjs(order?.startDateDisplay || '');
      const orderSlotEnd = dayjs(order?.endDateDisplay || '');

      // Check if blocked slot is completely within the order time
      const isWithin =
        blockedSlotStart.isSameOrAfter(orderSlotStart) &&
        blockedSlotEnd.isSameOrBefore(orderSlotEnd);

      // Check if blocked slot starts before and ends within the order time
      const startsBeforeEndsWithin =
        blockedSlotStart.isSameOrBefore(orderSlotStart) &&
        blockedSlotEnd.isSameOrAfter(orderSlotStart) &&
        blockedSlotEnd.isSameOrBefore(orderSlotEnd);

      // Check if blocked slot starts before order started and ends after order ends
      const startsBeforeEndsAfter =
        blockedSlotStart.isSameOrBefore(orderSlotStart) &&
        blockedSlotEnd.isSameOrAfter(orderSlotEnd);

      // Check if blocked slot starts after order started and ends after order ends
      const startsAfterEndsAfter =
        blockedSlotStart.isSameOrAfter(orderSlotStart) &&
        blockedSlotStart.isSameOrBefore(orderSlotEnd) &&
        blockedSlotEnd.isSameOrAfter(orderSlotEnd);

      return (
        isWithin ||
        startsBeforeEndsWithin ||
        startsBeforeEndsAfter ||
        startsAfterEndsAfter
      );
    };

    const clashingOrder: boolean = boardData.some((order) => {
      if (order.text === 'Blocked') return false;
      if (order.laneId !== formValues?.assetId) return false;

      return isBlockedSlotWithinOrder(order);
    });

    const currentBlockedSlot = boardData.find(
      (slot) =>
        slot.text === 'Blocked' &&
        slot.id === (formValues as EditBlockedSlotModel)?.blockedSlotId
    );
    const isCurrentBlockedSlotAuthorised =
      currentBlockedSlot?.state.toLowerCase() === 'authorized';
    const getDisabledHours = () => {
      const defaultDisabledHours = [0, 1, 2, 3, 4, 5, 6, 19, 20, 21, 22, 23];

      const startHour = tradingTimes?.start
        ? parseInt(tradingTimes?.start.split(':')[0])
        : 7;
      const endHour = tradingTimes?.end
        ? parseInt(tradingTimes?.end.split(':')[0])
        : 19;

      if (tradingTimes) {
        const beforeOpening = Array.from(
          { length: startHour },
          (_, index) => index
        );

        const afterClosing = Array.from(
          { length: 24 - endHour - 1 },
          (_, index) => endHour + index + 1
        );

        const disabledHours = beforeOpening.concat(afterClosing);

        return disabledHours;
      }

      return defaultDisabledHours;
    };

    const formatTime = (time: Dayjs | string) => {
      const timeStr = typeof time === 'string' ? time : time.format('HH:mm');
      const result = time
        ? dayjs(
            `${dayjs(selectedDate).format('YYYY-MM-DD')}T${timeStr}`
          ).format(DATE_FORMAT)
        : '';
      return result;
    };

    const handleSave = handleSubmit((data) => {
      const { startDateTime, finishDateTime } = data;

      // Formatted the start and finish date-time to remove milliseconds and Z as its somehow breaking the time calculations.
      const formattedStartDateTime = startDateTime.split('.')[0];
      const formattedFinishDateTime = finishDateTime.split('.')[0];

      const durationInMinutes = dayjs(formattedFinishDateTime).diff(
        dayjs(formattedStartDateTime),
        'minutes'
      );

      const formattedRequest = {
        ...data,
        startDateTime: data.startDateTime,
        finishDateTime: data.finishDateTime,
        serviceLocationId: selectedLocation?.id ?? '',
      };

      if (isEditMode) {
        editBlockedSlot({
          blockedSlotId: (formValues as EditBlockedSlotModel).blockedSlotId,
          assetId: formattedRequest.assetId,
          startDateTime: formattedRequest.startDateTime,
          finishDateTime: formattedRequest.finishDateTime,
          description: formattedRequest.description,
          serviceLocationId: formattedRequest.serviceLocationId,
        });

        if (
          durationInMinutes > 60 &&
          isCurrentBlockedSlotAuthorised &&
          isStoreManager
        ) {
          unauthorizeBlockedSlot({
            blockedSlotId: (formValues as EditBlockedSlotModel).blockedSlotId,
          });
        }
      } else if (hasAdminPermissions) {
        // Check if it has admin level to autoapprove
        createAndAuthorizeBlockedSlot({
          ...formattedRequest,
        });
      } else if (durationInMinutes < 60 && canAddBlockedSlot) {
        createAndAuthorizeBlockedSlot({
          ...formattedRequest,
        });
      } else {
        createBlockedSlot({
          ...formattedRequest,
        });
      }
    });

    const handleClose = () => {
      setIsEditMode(false);
      setModalOpen(false);
      reset();
    };

    const handleSafeModalClose = () => {
      if (isDirty) {
        unsavedChangesModal(() => setModalOpen(false));
      } else {
        handleClose();
      }
    };

    const {
      createAndAuthorizeBlockedSlot,
      createAndAuthorizeBlockedSlotIsLoading,
    } = useCreateAndAuthorizeBlockedSlot(handleClose);

    const { createBlockedSlot, createBlockedSlotIsLoading } =
      useCreateBlockedSlot(handleClose);

    const { editBlockedSlot, editBlockedSlotIsLoading } =
      useEditBlockedSlot(handleClose);

    const { authorizeBlockedSlot, authorizeBlockedSlotIsLoading } =
      useAuthorizeBlockedSlot(handleClose);

    const { unauthorizeBlockedSlot, unauthorizeBlockedSlotIsLoading } =
      useUnauthorizeBlockedSlot(handleClose);

    const { deleteBlockedSlot, deleteBlockedSlotIsLoading } =
      useDeleteBlockedSlot(handleClose);

    const handleBlockedSlotDelete = () => {
      confirmBlockedSlotDelete(() =>
        deleteBlockedSlot((formValues as EditBlockedSlotModel).blockedSlotId)
      );
    };

    useImperativeHandle(ref, () => {
      return {
        openModal: (laneId, date) => {
          const time = dayjs(date.format(DATE_FORMAT));

          reset({
            assetId: laneId,
            startDateTime: dayjs(time).utc(true).toISOString(),
            finishDateTime: dayjs(time).utc(true).toISOString(),
            description: '',
          });
          setModalOpen(true);
        },
        openEditModal: (blockedSlotData) => {
          reset({
            blockedSlotId: blockedSlotData.blockedSlotId,
            description: blockedSlotData.description,
            assetId: blockedSlotData.assetId,
            startDateTime: dayjs(
              blockedSlotData.startDateTime,
              'YYYY-MM-DDTHH:mm:ss'
            )
              .utc(true)
              .toISOString(),
            finishDateTime: dayjs(
              blockedSlotData.finishDateTime,
              'YYYY-MM-DDTHH:mm:ss'
            )
              .utc(true)
              .toISOString(),
          });
          setIsEditMode(true);
          setModalOpen(true);
        },
      };
    }, [reset]);

    return (
      <>
        <PosModal
          header={{ title: t('scheduling.blockedSlot.blockedSlot') }}
          open={open}
          onCancel={handleSafeModalClose}
          footer={
            <Flex gap={4} justify="space-between" style={fullWidth}>
              <Button
                danger
                loading={deleteBlockedSlotIsLoading}
                onClick={handleBlockedSlotDelete}
                hidden={!isEditMode}
              >
                {t('common.buttons.delete')}
              </Button>
              {isCurrentBlockedSlotAuthorised ? (
                <Button
                  danger
                  loading={unauthorizeBlockedSlotIsLoading}
                  onClick={() =>
                    unauthorizeBlockedSlot({
                      blockedSlotId: (formValues as EditBlockedSlotModel)
                        .blockedSlotId,
                    })
                  }
                  hidden={!isEditMode || !hasAdminPermissions}
                >
                  {t('common.buttons.unauthorise')}
                </Button>
              ) : (
                <Button
                  loading={authorizeBlockedSlotIsLoading}
                  onClick={() =>
                    authorizeBlockedSlot({
                      blockedSlotId: (formValues as EditBlockedSlotModel)
                        .blockedSlotId,
                    })
                  }
                  hidden={!isEditMode || !hasAdminPermissions}
                >
                  {t('common.buttons.authorise')}
                </Button>
              )}
              <Space style={{ flexGrow: 1, justifyContent: 'flex-end' }}>
                <Button onClick={handleSafeModalClose}>
                  {isDirty
                    ? t('common.buttons.cancel')
                    : t('common.buttons.close')}
                </Button>
                <Tooltip
                  title={
                    areStartEndTimesSame || isEndTimeBeforeStart
                      ? t(
                          'scheduling.blockedSlot.warningTooltipSameStartEndTimes'
                        )
                      : ''
                  }
                >
                  <Button
                    type="primary"
                    disabled={
                      isStartTimeBeforeCurrentTime ||
                      !isBlockedSlotValid ||
                      !isDirty ||
                      !isValid ||
                      formValues.description === ''
                    }
                    onClick={handleSave}
                    loading={
                      createAndAuthorizeBlockedSlotIsLoading ||
                      createBlockedSlotIsLoading ||
                      editBlockedSlotIsLoading
                    }
                  >
                    {isEditMode
                      ? t('common.buttons.update')
                      : t('common.buttons.send')}
                  </Button>
                </Tooltip>
              </Space>
            </Flex>
          }
        >
          <Flex vertical={true} style={{ marginBottom: token.marginSM }}>
            <Typography.Title level={3}>
              {isEditMode
                ? t('scheduling.blockedSlot.editBlockedSlot')
                : t('scheduling.blockedSlot.newBlockedSlot')}
            </Typography.Title>

            {clashingOrder ? (
              <PosText
                icon={faExclamationTriangle}
                color={token.colorWarningText}
              >
                {t('scheduling.blockedSlot.warningOverJob')}
              </PosText>
            ) : null}
            {isStartTimeBeforeCurrentTime ? (
              <PosText
                icon={faExclamationTriangle}
                color={token.colorWarningText}
              >
                {t('scheduling.blockedSlot.warningStartTime')}
              </PosText>
            ) : null}
          </Flex>

          <Form layout="vertical">
            <Row gutter={token.marginSM}>
              <Col span={12}>
                <Form.Item label={t('scheduling.blockedSlot.startTime')}>
                  <Controller
                    control={control}
                    name="startDateTime"
                    render={({
                      field: { name, value, onChange, ...field },
                    }) => (
                      <>
                        <SchedulingTimePicker
                          {...field}
                          value={value}
                          name={name}
                          hideDisabledOptions
                          returnFormat="YYYY-MM-DDTHH:mm"
                          defaultValue={dayjs(value, 'YYYY-MM-DDTHH:mm:ss')}
                          onChange={(time) => {
                            onChange(time && formatTime(time));
                            return trigger();
                          }}
                          minuteStep={15}
                          disabledTime={() => ({
                            disabledHours: () => getDisabledHours(),
                          })}
                        />

                        <ErrorMessage
                          errors={errors}
                          name={name}
                          render={() => {
                            return (
                              <Typography.Text type="danger">
                                {t('scheduling.blockedSlot.invalidTime')}
                              </Typography.Text>
                            );
                          }}
                        />
                      </>
                    )}
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item label={t('scheduling.blockedSlot.endTime')}>
                  <Controller
                    control={control}
                    name="finishDateTime"
                    render={({
                      field: { name, value, onChange, ...field },
                    }) => (
                      <>
                        <SchedulingTimePicker
                          {...field}
                          value={value}
                          name={name}
                          hideDisabledOptions
                          returnFormat="YYYY-MM-DDTHH:mm"
                          defaultValue={dayjs(value, 'YYYY-MM-DDTHH:mm:ss')}
                          onChange={(time) => {
                            onChange(time && formatTime(time));
                            return trigger();
                          }}
                          minuteStep={15}
                          disabledTime={() => ({
                            disabledHours: () => getDisabledHours(),
                          })}
                        />

                        <ErrorMessage
                          errors={errors}
                          name={name}
                          render={({ message }) => {
                            return (
                              <Typography.Text type="danger">
                                {message.includes('endTimeSmaller')
                                  ? t('scheduling.blockedSlot.endTimeSmaller')
                                  : t('scheduling.blockedSlot.invalidTime')}
                              </Typography.Text>
                            );
                          }}
                        />
                      </>
                    )}
                  />
                </Form.Item>
              </Col>

              <Col span={24}>
                <PosInput
                  label={t('common.reason.reason')}
                  name="description"
                  control={control}
                  required
                />
              </Col>
            </Row>
          </Form>
        </PosModal>
      </>
    );
  }
);

export default BlockedSlotModal;
